import { statementUploadInputFingerprintV2 } from "@tradvera/portfolio-domain";

export type StatementUploadChoiceV2 = "CSV" | "OFX" | "QFX" | "PDF" | "JPG" | "PNG" | "WEBP" | "TIFF";

export interface PreparedBrowserStatementUploadV2 {
  choice: StatementUploadChoiceV2;
  file: File;
  fileSha256: string;
  mediaType: string;
  bytes: number;
  idempotencyKey: string;
  inputFingerprint: string;
}

export const STATEMENT_UPLOAD_CHOICES_V2: Readonly<Record<StatementUploadChoiceV2, {
  extensions: readonly string[]; accept: string; mediaType: string;
}>> = Object.freeze({
  CSV: { extensions: [".csv"], accept: ".csv,text/csv", mediaType: "text/csv" },
  OFX: { extensions: [".ofx"], accept: ".ofx,application/x-ofx", mediaType: "application/x-ofx" },
  QFX: { extensions: [".qfx"], accept: ".qfx,application/x-ofx", mediaType: "application/x-ofx" },
  PDF: { extensions: [".pdf"], accept: ".pdf,application/pdf", mediaType: "application/pdf" },
  JPG: { extensions: [".jpg", ".jpeg"], accept: ".jpg,.jpeg,image/jpeg", mediaType: "image/jpeg" },
  PNG: { extensions: [".png"], accept: ".png,image/png", mediaType: "image/png" },
  WEBP: { extensions: [".webp"], accept: ".webp,image/webp", mediaType: "image/webp" },
  TIFF: { extensions: [".tif", ".tiff"], accept: ".tif,.tiff,image/tiff", mediaType: "image/tiff" },
});

export const STATEMENT_UPLOAD_ACCEPT_V2 = Object.values(STATEMENT_UPLOAD_CHOICES_V2)
  .flatMap((choice) => choice.accept.split(",")).filter((value, index, all) => all.indexOf(value) === index).join(",");

export function statementUploadChoiceForFileV2(file: Pick<File, "name" | "type">): StatementUploadChoiceV2 {
  const lower = file.name.toLowerCase();
  const extension = (Object.keys(STATEMENT_UPLOAD_CHOICES_V2) as StatementUploadChoiceV2[])
    .find((choice) => STATEMENT_UPLOAD_CHOICES_V2[choice].extensions.some((item) => lower.endsWith(item)));
  if (extension) return extension;
  const declared = file.type.split(";", 1)[0]?.trim().toLowerCase();
  if (declared === "text/csv" || declared === "application/csv" || declared === "text/tab-separated-values") return "CSV";
  if (declared === "application/x-ofx" || declared === "application/ofx") return "OFX";
  if (declared === "application/pdf") return "PDF";
  if (declared === "image/jpeg" || declared === "image/jpg") return "JPG";
  if (declared === "image/png") return "PNG";
  if (declared === "image/webp") return "WEBP";
  if (declared === "image/tiff") return "TIFF";
  throw new Error("STATEMENT_IMPORT_FORMAT_UNSUPPORTED");
}

export async function prepareBrowserStatementUploadV2(input: {
  file: File; choice: StatementUploadChoiceV2; bookId: string; idempotencyKey?: string;
}): Promise<PreparedBrowserStatementUploadV2> {
  validateStatementFileV2(input.file, input.choice);
  const idempotencyKey = input.idempotencyKey ?? newStatementUploadIdempotencyKeyV2();
  const fileSha256 = await browserFileSha256V2(input.file);
  const inputFingerprint = statementUploadInputFingerprintV2({
    schemaVersion: "tradvera.portfolio-v2.statement-upload.v1",
    bookId: input.bookId,
    idempotencyKey,
    fileSha256,
    bytes: input.file.size,
  });
  return { choice: input.choice, file: input.file, fileSha256,
    mediaType: STATEMENT_UPLOAD_CHOICES_V2[input.choice].mediaType, bytes: input.file.size,
    idempotencyKey, inputFingerprint };
}

export async function browserFileSha256V2(file: Pick<File, "arrayBuffer" | "size">): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new Error("STATEMENT_IMPORT_SECURE_HASH_UNAVAILABLE");
  if (!Number.isSafeInteger(file.size) || file.size < 1 || file.size > 25 * 1024 * 1024) {
    throw new Error(file.size < 1 ? "STATEMENT_IMPORT_FILE_EMPTY" : "STATEMENT_IMPORT_FILE_TOO_LARGE");
  }
  const bytes = await file.arrayBuffer();
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
}

export function validateStatementFileV2(file: Pick<File, "name" | "size">,
  choice: StatementUploadChoiceV2): void {
  if (!file.name || file.name.length > 255 || /[\\/\u0000-\u001f\u007f]/u.test(file.name)) {
    throw new Error("STATEMENT_IMPORT_FILE_REQUIRED");
  }
  if (!Number.isSafeInteger(file.size) || file.size < 1) throw new Error("STATEMENT_IMPORT_FILE_EMPTY");
  if (file.size > 25 * 1024 * 1024) throw new Error("STATEMENT_IMPORT_FILE_TOO_LARGE");
  const lower = file.name.toLowerCase();
  if (!STATEMENT_UPLOAD_CHOICES_V2[choice].extensions.some((extension) => lower.endsWith(extension))) {
    throw new Error("STATEMENT_IMPORT_FORMAT_UNSUPPORTED");
  }
  // This is only an early UX check. The backend remains authoritative and
  // validates magic bytes/content rather than trusting this extension.
}

export function newStatementUploadIdempotencyKeyV2(): string {
  if (!globalThis.crypto?.randomUUID) throw new Error("STATEMENT_IMPORT_SECURE_OPERATION_ID_UNAVAILABLE");
  return `statement-upload:${globalThis.crypto.randomUUID()}`;
}

